/**
 * NeoCorePanelVivoPage — VIDEOWALL de tesorería en vivo (estilo NOC/SOC).
 *
 * Dos flujos enfrentados: ENTRA (aportes, ahorro, pagos de crédito) vs SALE
 * (desembolsos). Cada evento nuevo entra animado en su columna (stream real, no
 * loop falso). Totales de hoy con conteo animado + barra de flujo neto. Refresca
 * cada 8s y detecta eventos nuevos para animarlos.
 *
 * Fuente: RPC flujo_vivo(). Pensado para dejar en un videowall a pantalla completa.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'

const REFRESH_MS = 8_000
const IN = '#22C58B'
const OUT = '#FF6A45'
const GRID = '#16181D'

type Cat = { hoy: number; total: number }
type Agg = { hoy_monto: number; hoy_n: number; total_monto: number; total_n: number; cats?: Record<string, Cat> }
type Ev = { dir: 'in' | 'out'; cat: string; nombre: string; monto: number; at: string }
type Flujo = { generado_at: string; hoy: string; entra: Agg; sale: Agg; feed: Ev[] }

const Q = (n: number) => `Q${Math.round(n).toLocaleString('en-US')}`
const cap = (s: string) => s.split(' ').slice(0, 3).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
const keyOf = (e: Ev) => `${e.dir}|${e.at}|${e.monto}|${e.nombre}`
const CAT_LABEL: Record<string, string> = { aporte: 'Aporte', ahorro: 'Ahorro', pago: 'Pago crédito', desembolso: 'Desembolso' }

const CSS = `
@keyframes pvIn{0%{opacity:0;transform:translateY(-14px);background:rgba(255,255,255,.10)}60%{opacity:1}100%{opacity:1;transform:none;background:transparent}}
@keyframes pvPulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes pvScan{0%{transform:translateX(-100%)}100%{transform:translateX(100%)}}
.pv-row{animation:pvIn .7s cubic-bezier(.22,1,.36,1) both;border-bottom:1px solid ${GRID}}
.pv-live{width:9px;height:9px;border-radius:50%;animation:pvPulse 1.3s ease-in-out infinite}
.pv-scan{position:absolute;inset:0;overflow:hidden;pointer-events:none}
.pv-scan::before{content:"";position:absolute;top:0;left:0;width:40%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.03),transparent);animation:pvScan 7s linear infinite}
`

function Count({ value, prefix = 'Q' }: { value: number; prefix?: string }) {
  const [v, setV] = useState(value); const prev = useRef(value)
  useEffect(() => {
    const from = prev.current, to = value; prev.current = value
    if (from === to) { setV(to); return }
    let raf = 0, t0 = 0; const dur = 800
    const tick = (ts: number) => { if (!t0) t0 = ts; const p = Math.min(1, (ts - t0) / dur); setV(from + (to - from) * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(tick) }
    raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf)
  }, [value])
  return <>{prefix}{Math.round(v).toLocaleString('en-US')}</>
}

export function NeoCorePanelVivoPage() {
  const [d, setD] = useState<Flujo | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [now, setNow] = useState(() => new Date())
  const seen = useRef<Set<string>>(new Set())
  const [fresh, setFresh] = useState<Set<string>>(new Set())

  const load = useCallback(() => {
    supabase.rpc('flujo_vivo').then(({ data, error }) => {
      if (error) { setErr(error.message); return }
      setErr(null)
      const f = data as Flujo
      const nuevos = new Set<string>()
      for (const e of f.feed ?? []) { const k = keyOf(e); if (!seen.current.has(k)) { nuevos.add(k); seen.current.add(k) } }
      setFresh(nuevos)
      setD(f)
    })
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => { const t = setInterval(load, REFRESH_MS); return () => clearInterval(t) }, [load])
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])

  if (err && !d) return <div style={{ padding: 40, color: '#888' }}>{err}</div>
  if (!d) return <div style={{ padding: 40, color: '#888' }}>Conectando al flujo…</div>

  const inFeed = (d.feed ?? []).filter((e) => e.dir === 'in').slice(0, 14)
  const outFeed = (d.feed ?? []).filter((e) => e.dir === 'out').slice(0, 14)
  const entraHoy = d.entra.hoy_monto, saleHoy = d.sale.hoy_monto
  const neto = entraHoy - saleHoy
  const totalFlow = Math.max(1, entraHoy + saleHoy)
  const inPct = Math.round((entraHoy / totalFlow) * 100)
  const cats = d.entra.cats ?? {}

  return (
    <div style={{
      background: '#08090B', borderRadius: 16, padding: 0, color: '#E9E9EC',
      minHeight: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column',
      fontFamily: 'Noto Sans, system-ui, sans-serif', fontVariantNumeric: 'tabular-nums', overflow: 'hidden', position: 'relative',
    }}>
      <style>{CSS}</style>
      <div className="pv-scan" />

      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 22px', borderBottom: `1px solid ${GRID}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/brand/isotipo-naranja.svg" alt="" style={{ width: 26, height: 26 }} />
          <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>Neocoop · Flujo en vivo</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 13, color: '#7B7B84', letterSpacing: 1 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums', color: '#B9B9C0', fontSize: 15 }}>{now.toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/Guatemala' })}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span className="pv-live" style={{ background: IN }} /> EN VIVO</span>
        </div>
      </div>

      {/* Barra de flujo neto */}
      <div style={{ padding: '14px 22px 6px', borderBottom: `1px solid ${GRID}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#7B7B84', marginBottom: 7, letterSpacing: 1, textTransform: 'uppercase' }}>
          <span style={{ color: IN }}>▲ Entra hoy · <Count value={entraHoy} /></span>
          <span style={{ color: neto >= 0 ? IN : OUT, fontWeight: 700 }}>Neto {neto >= 0 ? '+' : '−'}{Q(Math.abs(neto))}</span>
          <span style={{ color: OUT }}>Sale hoy · <Count value={saleHoy} /> ▼</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', background: OUT }}>
          <div style={{ width: `${inPct}%`, background: IN, transition: 'width 1s cubic-bezier(.22,1,.36,1)' }} />
        </div>
      </div>

      {/* Dos columnas de flujo */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, minHeight: 0 }}>
        <FlowCol side="in" color={IN} title="Dinero que entra"
          hoyN={d.entra.hoy_n} hoyMonto={entraHoy} totalMonto={d.entra.total_monto}
          chips={[
            { l: 'Aportes', v: cats.aporte?.hoy ?? 0 },
            { l: 'Ahorro', v: cats.ahorro?.hoy ?? 0 },
            { l: 'Pagos', v: cats.pago?.hoy ?? 0 },
          ]}
          feed={inFeed} fresh={fresh} />
        <FlowCol side="out" color={OUT} title="Dinero que sale"
          hoyN={d.sale.hoy_n} hoyMonto={saleHoy} totalMonto={d.sale.total_monto}
          chips={[{ l: 'Desembolsos hoy', v: null, n: d.sale.hoy_n }]}
          feed={outFeed} fresh={fresh} borderLeft />
      </div>
    </div>
  )
}

function FlowCol({ side, color, title, hoyN, hoyMonto, totalMonto, chips, feed, fresh, borderLeft }:
  { side: 'in' | 'out'; color: string; title: string; hoyN: number; hoyMonto: number; totalMonto: number
    chips: { l: string; v: number | null; n?: number }[]; feed: Ev[]; fresh: Set<string>; borderLeft?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderLeft: borderLeft ? `1px solid ${GRID}` : undefined }}>
      <div style={{ padding: '18px 22px 14px', borderBottom: `1px solid ${GRID}` }}>
        <div style={{ fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: '#7B7B84', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color }}>{side === 'in' ? '▲' : '▼'}</span>{title}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
          <span style={{ fontSize: 54, fontWeight: 800, letterSpacing: -1.5, lineHeight: 0.9, color }}><Count value={hoyMonto} /></span>
          <span style={{ fontSize: 13, color: '#7B7B84' }}>hoy · {hoyN} mov.</span>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
          {chips.map((c) => (
            <div key={c.l}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#D6D6DB' }}>{c.v != null ? Q(c.v) : (c.n ?? 0)}</div>
              <div style={{ fontSize: 11, color: '#6A6A72' }}>{c.l}</div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#8A8A92' }}>{Q(totalMonto)}</div>
            <div style={{ fontSize: 11, color: '#5A5A62' }}>acumulado</div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', padding: '2px 0' }}>
        {feed.length === 0 ? (
          <div style={{ padding: 24, color: '#5A5A62', fontSize: 13 }}>Sin movimientos.</div>
        ) : feed.map((e) => (
          <div key={keyOf(e)} className={fresh.has(keyOf(e)) ? 'pv-row' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 22px', borderBottom: `1px solid ${GRID}` }}>
            <span style={{ fontSize: 11, color: '#5A5A62', width: 42, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{hhmm(e.at)}</span>
            <span style={{ width: 6, height: 6, borderRadius: 6, background: color, flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#D6D6DB', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cap(e.nombre)}</span>
            <span style={{ fontSize: 10.5, color: '#7B7B84', textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0 }}>{CAT_LABEL[e.cat] ?? e.cat}</span>
            <span style={{ fontSize: 15, fontWeight: 800, color, width: 92, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{side === 'in' ? '+' : '−'}{Q(e.monto)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default NeoCorePanelVivoPage
